import type { Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxMaybeArray } from '@onyx/angular';
import { OnyxPhonePipe } from '@onyx/angular';
import { castArray } from 'lodash';
import type { z } from 'zod';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ExportHelper } from '../../../common/helpers/export.helper';
import { DriversLicenseCategory } from '../enums/drivers-license-category';
import type { Driver, DriverIdentityDocument } from '../interfaces/driver';
import type { getDriversXlsxSchema } from './drivers-xlsx-schema';

export type DriversExportData = z.input<
  ReturnType<typeof getDriversXlsxSchema>
>;

export const getDriversExportData = (
  data: OnyxMaybeArray<Driver>,
  injector: Injector,
): DriversExportData[] => {
  const translateService = injector.get(TranslateService);
  const phonePipe = injector.get(OnyxPhonePipe);

  return castArray(data).map((driver) => {
    const {
      driverData,
      employmentConditions,
      skills,
      countries,
      note,
      driverCard,
      driversLicenseAndProfessionalQualifications,
      identityDocuments,
      clearCriminalRecordCertificate,
    } = driver;

    const getLicenseDate = (
      category: DriversLicenseCategory,
    ): string | null => {
      const license =
        driversLicenseAndProfessionalQualifications.categories.find(
          (item) => item.category === category,
        );
      if (!license) return null;

      return license.expirationDate.indefinite
        ? 'labels.indefinite'
        : license.expirationDate.date!;
    };

    const getDocumentFields = (document: DriverIdentityDocument | null) => {
      if (!document) {
        return {
          number: null,
          issuingCountry: null,
          expirationDate: null,
        };
      }

      return {
        number: document.number,
        issuingCountry: ExportHelper.mapField(
          document.issuingCountry,
          DictionaryCode.COUNTRY,
        ),
        expirationDate: document.expirationDate,
      };
    };

    const driverCardFields = getDocumentFields(driverCard);
    const passportFields = getDocumentFields(identityDocuments.passport);
    const idFields = getDocumentFields(identityDocuments.identityDocument);
    const residenceFields = getDocumentFields(identityDocuments.residenceCard);
    const polishCardFields = getDocumentFields(identityDocuments.polishCard);

    return {
      /* driverData */
      firstName: driverData.firstName,
      lastName: driverData.lastName,
      peselNumber: driverData.peselNumber,
      birthDate: driverData.birthDate,
      privatePhone: phonePipe.transform(driverData.privatePhone),
      workPhone: driverData.workPhone
        ? phonePipe.transform(driverData.workPhone)
        : null,
      privateEmail: driverData.privateEmail ?? null,
      nationality: ExportHelper.mapField(
        driverData.nationality,
        DictionaryCode.COUNTRY,
      ),

      homeAddress: driverData.homeAddress.label,
      assignedBase: driverData.assignedBase?.name ?? null,

      /* employmentConditions */
      contractType: ExportHelper.mapField(
        employmentConditions.contractType,
        DictionaryCode.CONTRACT_TYPE,
      ),
      settlementMethod: ExportHelper.mapField(
        employmentConditions.settlementMethod,
        DictionaryCode.DRIVER_SETTLEMENT_METHOD,
      ),
      dailyRate: employmentConditions.dailyRate?.value ?? null,
      dailyRateCurrency: ExportHelper.mapField(
        employmentConditions.dailyRate?.currency,
        DictionaryCode.CURRENCY,
      ),
      mileageRate: employmentConditions.mileageRate?.value ?? null,
      mileageRateCurrency: ExportHelper.mapField(
        employmentConditions.mileageRate?.currency,
        DictionaryCode.CURRENCY,
      ),
      baseRate: employmentConditions.baseRate?.value ?? null,
      baseRateCurrency: ExportHelper.mapField(
        employmentConditions.baseRate?.currency,
        DictionaryCode.CURRENCY,
      ),
      freightPercentage: employmentConditions.freightPercentage,
      roundTripRate: employmentConditions.roundTripRate?.value ?? null,
      roundTripRateCurrency: ExportHelper.mapField(
        employmentConditions.roundTripRate?.currency,
        DictionaryCode.CURRENCY,
      ),
      contractTermFrom: employmentConditions.contractTerm.from!,
      contractTermTo: employmentConditions.contractTerm.indefinite
        ? translateService.instant('labels.indefinite')
        : employmentConditions.contractTerm.to!,
      vatId: employmentConditions.nipNumber,

      /* skills */
      languages: ExportHelper.mapField(
        skills.languages,
        DictionaryCode.LANGUAGE,
        translateService,
      ),
      technicalSkills: ExportHelper.mapField(
        skills.technicalSkills,
        DictionaryCode.DRIVER_TECHNICAL_SKILL,
        translateService,
      ),
      semiTrailers: ExportHelper.mapField(
        skills.semiTrailers,
        DictionaryCode.VEHICLE_TYPE,
        translateService,
      ),
      experienceLevel: ExportHelper.mapField(
        skills.experienceLevel,
        DictionaryCode.DRIVER_EXPERIENCE_LEVEL,
      ),

      /* countriesWhitelist */
      countriesGroup: ExportHelper.mapField(
        countries.group,
        DictionaryCode.COUNTRIES_GROUP,
      ),
      countriesWhitelist: !countries.group
        ? ExportHelper.mapField(
            countries.whitelist,
            DictionaryCode.COUNTRY,
            translateService,
          )
        : null,

      /* note */
      note: note?.content ?? null,

      /* driversLicenseAndProfessionalQualifications */
      [`driversLicenseCategory.${DriversLicenseCategory.B}`]: getLicenseDate(
        DriversLicenseCategory.B,
      ),
      [`driversLicenseCategory.${DriversLicenseCategory.B1}`]: getLicenseDate(
        DriversLicenseCategory.B1,
      ),
      [`driversLicenseCategory.${DriversLicenseCategory.BE}`]: getLicenseDate(
        DriversLicenseCategory.BE,
      ),
      [`driversLicenseCategory.${DriversLicenseCategory.C}`]: getLicenseDate(
        DriversLicenseCategory.C,
      ),
      [`driversLicenseCategory.${DriversLicenseCategory.C1}`]: getLicenseDate(
        DriversLicenseCategory.C1,
      ),
      [`driversLicenseCategory.${DriversLicenseCategory.C1E}`]: getLicenseDate(
        DriversLicenseCategory.C1E,
      ),
      [`driversLicenseCategory.${DriversLicenseCategory.CE}`]: getLicenseDate(
        DriversLicenseCategory.CE,
      ),

      professionalQualificationsExpirationDate:
        driversLicenseAndProfessionalQualifications.professionalQualificationsExpirationDate,
      driversLicenseNumber:
        driversLicenseAndProfessionalQualifications.driversLicenseNumber,
      driversLicenseIssuingCountry: ExportHelper.mapField(
        driversLicenseAndProfessionalQualifications.driversLicenseIssuingCountry,
        DictionaryCode.COUNTRY,
      ),

      /* driverCard  */
      driverCardNumber: driverCardFields.number,
      driverCardIssuingCountry: driverCardFields.issuingCountry,
      driverCardExpirationDate: driverCardFields.expirationDate,

      /* identityDocuments */
      passportNumber: passportFields.number,
      passportIssuingCountry: passportFields.issuingCountry,
      passportExpirationDate: passportFields.expirationDate,

      identityDocumentNumber: idFields.number,
      identityDocumentIssuingCountry: idFields.issuingCountry,
      identityDocumentExpirationDate: idFields.expirationDate,

      residenceCardNumber: residenceFields.number,
      residenceCardIssuingCountry: residenceFields.issuingCountry,
      residenceCardExpirationDate: residenceFields.expirationDate,

      polishCardNumber: polishCardFields.number,
      polishCardIssuingCountry: polishCardFields.issuingCountry,
      polishCardExpirationDate: polishCardFields.expirationDate,

      /* clearCriminalRecordCertificate */
      clearCriminalRecordCertificateIssuingDate:
        clearCriminalRecordCertificate.issuingDate,
    } satisfies DriversExportData;
  });
};
